import { Card, Group, Text, Title, UnstyledButton } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useState } from 'react';

import { formatearEur, formatearNumero } from '../formato';

/**
 * Una fila de distribución, ya normalizada.
 *
 * El contrato tipa cada desglose por su dimensión (`regionName`, `caesMitecoStatus`,
 * `caesPaymentState`), que es más autodescriptivo para el backend. La UI necesita lo contrario: una
 * forma común para pintar los tres igual. La conversión vive en `ResumenPanel` y son tres líneas —
 * mucho más barato que pedir un cambio de contrato, y el contrato es de Rubén.
 */
export interface FilaDistribucion {
  clave: string | null;
  nViajes: number;
  importeEur: string;
}

/**
 * DECISIONES DE VISUALIZACION (y por qué, para que no se "mejoren" luego):
 *
 * - UN SOLO TONO para todas las barras. Esto es UNA serie (importe por categoría), no N series.
 *   Pintar cada CA de un color distinto sería un value-ramp sobre categorías nominales: duplica en
 *   color lo que la longitud de la barra ya dice, y quema el único canal libre que queda.
 * - Ese canal libre se gasta en lo único que significa algo: `CANCELED` y `FAILED`. Como el estado
 *   de pago no es filtrable, este es el único sitio donde Ops los ve antes de confirmar. Van con
 *   color de estado + un glifo + su nombre: tres codificaciones, nunca color solo.
 * - La barra escala al MAXIMO de la dimensión, no al total, porque así se distinguen las categorías
 *   pequeñas entre sí; escalando al total, con 12 CAs la última sería un pelo invisible. El coste es
 *   que la barra más larga sale llena y puede leerse como "esto es todo", y por eso cada fila lleva
 *   su cuota sobre el total al lado: la barra da la forma, el número da la proporción.
 * - La cola se pliega pero NO se suma: se dice cuántas categorías quedan ocultas. Sumarlas sería
 *   aritmética de dinero en el cliente.
 * - El bucket "sin valor" (clave null) NUNCA se pliega: es justo el que hace que el total no cuadre
 *   si se esconde.
 */
const ENFASIS: Record<string, { clase: string }> = {
  FAILED: { clase: 'barra-critica' },
  CANCELED: { clase: 'barra-aviso' },
};

const TOPE_VISIBLE = 8;

function Fila({ fila, maximo, total }: { fila: FilaDistribucion; maximo: number; total: number }) {
  const enfasis = fila.clave ? ENFASIS[fila.clave] : undefined;
  const etiqueta = fila.clave ?? 'Sin valor';
  const importe = Number(fila.importeEur);
  // Longitud de barra y porcentaje son presentación, igual que formatear: el importe que se LEE es
  // el que manda el backend, sin tocar.
  const proporcion = maximo > 0 ? (importe / maximo) * 100 : 0;
  const cuota = total > 0 ? (importe / total) * 100 : 0;

  return (
    <div className="dist-fila" title={`${etiqueta} · ${formatearNumero(fila.nViajes)} viajes`}>
      <div className="dist-etiqueta">
        {enfasis && <IconAlertTriangle size={13} aria-hidden="true" className="dist-glifo" />}
        <span className={fila.clave === null ? 'sin-valor' : undefined}>{etiqueta}</span>
      </div>
      <div className="dist-pista">
        <div
          className={`dist-barra ${enfasis?.clase ?? ''}`}
          style={{ width: `${Math.max(proporcion, 0.5)}%` }}
        />
      </div>
      <div className="dist-importe">{formatearEur(fila.importeEur)}</div>
      <div className="dist-cuota">{cuota < 1 && cuota > 0 ? '<1' : Math.round(cuota)}%</div>
      <div className="dist-viajes">{formatearNumero(fila.nViajes)}</div>
    </div>
  );
}

export function Distribucion({
  titulo,
  filas,
  totalEur,
}: {
  titulo: string;
  filas: FilaDistribucion[];
  totalEur: string;
}) {
  const [desplegada, setDesplegada] = useState(false);

  const ordenadas = [...filas].sort((a, b) => Number(b.importeEur) - Number(a.importeEur));
  const maximo = Math.max(0, ...ordenadas.map((f) => Number(f.importeEur)));
  const total = Number(totalEur);

  const cabeza = ordenadas.slice(0, TOPE_VISIBLE);
  const cola = ordenadas.slice(TOPE_VISIBLE);
  const sinValorEnCola = cola.filter((f) => f.clave === null);
  const colaPlegable = cola.filter((f) => f.clave !== null);
  const visibles = desplegada ? ordenadas : [...cabeza, ...sinValorEnCola];

  return (
    <Card withBorder padding="md">
      <Group justify="space-between" mb="sm">
        <Title order={3} fz="h5">
          {titulo}
        </Title>
        <Text size="xs" c="dimmed">
          {ordenadas.length} {ordenadas.length === 1 ? 'valor' : 'valores'}
        </Text>
      </Group>

      <div className="dist">
        {visibles.map((fila) => (
          <Fila key={fila.clave ?? '__sin_valor__'} fila={fila} maximo={maximo} total={total} />
        ))}
      </div>

      {colaPlegable.length > 0 && (
        <UnstyledButton className="dist-desplegar" onClick={() => setDesplegada((v) => !v)}>
          {desplegada ? 'Ver solo los mayores' : `Ver ${colaPlegable.length} valores mas`}
        </UnstyledButton>
      )}
    </Card>
  );
}
